import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import APIService, { TProfile } from 'services/api-service'
import getProfileParser from 'workers/get-profile-parser.js'
import * as ProfileUtilities from './ProfileUtilities'
import { parseMachineLists } from 'utils/api-utilities'
import { TFilterState } from 'components/filters/SmartFilter/SmartFilterTypes'
import { frequencies, months } from 'services/lookup-service'
import { TMonth } from 'components/filters/ConfiguredFilters/FiscalYearStartMonthFilter'
import ReactDOM from 'react-dom'
import { InvoiceStatus as InvoicePaymentStatus } from 'components/FilterAccordion/types'
import {
  getIsInternalUser,
  isAnalyticsCustomer,
  isFinancialRestricted,
  isSullairCustomer,
  isUSSteelCustomer,
} from 'utils/customer-utilities'
import { CancellablePromise } from 'utils/CancellablePromise'
import { useAuth0 } from '@auth0/auth0-react'
import useLogout from 'hooks/useAccessToken/useAuth0Wrapper'
import useLoggedNavigate from 'hooks/useLoggedNavigate'
import useAccessPermissions from 'hooks/useAccessToken/useAccessPermissions'
import { XAPIPermissions } from 'types/custom.d'
import LoginService from 'services/login-service'
import { useAccessToken } from 'hooks/useAccessToken/useAccessToken'
import { filterProfileOptions } from 'components/filters/SmartFilter/useOptionFilter'
import { defaultFilterState } from 'components/filters/SmartFilter/constants'
import { LocalStorageManager } from 'utils/LocalStorageManager'

export type TOrganizationScope = {
  customerID: number
  plantIDs: Set<number> | null
}

type TOrganizationScopeFunction = (
  prev: TOrganizationScope | null
) => TOrganizationScope

export type TProfileUtilities = {
  setImposterProfileID: (id: string | null) => void
  clearImposterProfileID: () => void
  reloadProfile: () => Promise<void>
  clearProfile: () => Promise<void>
  constructProfile: () => Promise<void>
  // setUserLoggedIn: (loggedIn: boolean) => void
  reparseMachineLists: () => Promise<void>
  getFilterOptions: () => Promise<Partial<TFilterState>>
  setOrganizationScope: (func: TOrganizationScopeFunction) => void
}

export type TUserDetails = {
  isDivisionUser: boolean
  isMultiCustomerUser: boolean
  isMultiPlantUser: boolean
  isReadonlyUser: boolean
  isUPSUser: boolean
  isSullairUser: boolean
  isUSSteelUser: boolean
  isAnalyticsCustomer: boolean
  hasCompany: boolean
  isFinancialRestricted: boolean
  isInternalUser: boolean
  canImposter: boolean
  imposterProfileID: string | null
}

export type TFilterInformation = {
  organizationScope: TOrganizationScope | null
  filterOptions: Partial<TFilterState>
  initialFilterOptionsLoaded: boolean
  minimumFilterOptionsLoaded: boolean
  fullFilterOptionsLoaded: boolean
  initialFilterState: Partial<TFilterState>
  defaultValues: Partial<TAccordionDefaults>
}

export type TProfileDependentData = {
  filters: TFilterInformation
  userDetails: Partial<TUserDetails>
}

export type TOnLogoutEvents = {
  AddEvent: (func: () => void) => void
  RemoveEvent: (func: () => void) => void
}

export type TUserSessionManagement = {
  onLogoutEvents: TOnLogoutEvents
}

export interface TProfileContext {
  readyToLoad: boolean
  minimumProfileLoaded: boolean
  fullProfileLoaded: boolean
  billingCustomersLoaded: boolean
  profile: Partial<TProfile>
  dependentData: TProfileDependentData
  utilities: TProfileUtilities
  userSessionManagement: TUserSessionManagement
}

const ongoingCancellablePromises = [] as CancellablePromise<unknown>[]

export const profileDefaults: Partial<TProfile> = {
  user: null,
  correctiveActions: [],
  custEquIDs: [],
  customers: [],
  divisions: [],
  error: '',
  lubeMfrOptions: [],
  lubeTypes: [],
  machineMfgOptions: [],
  machineMfgs: [],
  machineModels: [],
  machineNames: [],
  machineNos: [],
  machineTypeOptions: [],
  machineTypes: [],
  machines: [],
  plants: [],
  routes: [],
  shippingInformation: [],
  testGroups: [],
  billingCustomers: [],
}

export const defaultValues = {
  startDate: (() => {
    const now = new Date(Date.now())
    const startOfMonth = new Date(now.getFullYear(), now.getMonth() - 3, 1)
    return startOfMonth
  })(),

  endDate: (() => {
    const now = new Date(Date.now())
    return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
  })(),
  hideCompleted: false,
  sampleDate: null,
  frequency: frequencies.find(f => f.text === 'Yearly'),
  reportDateSampleDate: 'Sample Date',
  hideReviewed: false,
  showReportDate: false,
  showSampleDate: true,
  labNumber: '',
  serialNumber: '',
  sampleStatus: null,
  fiscalYearStartMonth: months[0] as TMonth,
  daysDelinquent: 90 as 0 | 90 | 30 | 45 | 60 | 75 | 120 | 240 | 360,
  exceptionCount: 2,
  omitDayOfMonth: true,
  omitFullDate: false,
  shipmentStatuses: [],
  orderNumber: null,
  trackingNumber: null,
  smartFilterPrefiltering: true,
  onlyForSelectedMonth: false,
  invoiceStatus: InvoicePaymentStatus.Unpaid as InvoicePaymentStatus,
}

export type TAccordionDefaults = typeof defaultValues

export const useProfile = () => {
  return useContext(ProfileContext)
}

export const ProfileContext = React.createContext<TProfileContext>(
  {} as TProfileContext
)
ProfileContext.displayName = 'ProfileContext'

let logoutEvents: Array<() => void> = []
export const ProfileContextProvider = (props: React.PropsWithChildren) => {
  const { isAuthenticated } = useAuth0()
  const logout = useLogout()
  const accessToken = useAccessToken()
  const navigate = useLoggedNavigate()
  const permissions = useAccessPermissions()

  const [profile, setProfile] = useState<Partial<TProfile>>(profileDefaults)
  const [profileErrorState, setProfileErrorState] = useState<unknown | null>(
    null
  )

  const [allFilterOptions, setAllFilterOptions] = useState<TFilterState>({
    ...defaultFilterState,
  })

  const [organizationScope, setOrganizationScope] =
    useState<TOrganizationScope | null>(null)

  const [currentFilterOptions, setFilterOptions] = useState<TFilterState>({
    ...defaultFilterState,
  })

  const [initialFilterOptionsLoaded, setInitialFilterOptionsLoaded] =
    useState(false)
  const [minimumFilterOptionsLoaded, setMinimumFilterOptionsLoaded] =
    useState(false)
  const [fullFilterOptionsLoaded, setFullFilterOptionsLoaded] = useState(false)
  const [userDetails, setUserDetails] = useState<Partial<TUserDetails>>({})

  const [minimumProfileLoaded, setMinimumProfileLoaded] = useState(false)
  const [fullProfileLoaded, setFullProfileLoaded] = useState(false)
  const [billingCustomersLoaded, setBillingCustomersLoaded] = useState(false)
  // const [loggedIn, setUserLoggedIn] = useState(false)

  const [profileLoading, setProfileLoading] = useState(false)

  const [imposterProfileID, setImposterProfileIDInternal] = useState<
    string | null
  >(null)

  useEffect(() => {
    if (!isAuthenticated) {
      let event: () => void
      while ((event = logoutEvents.pop())) {
        event()
      }
    }
  }, [isAuthenticated])

  const setImposterProfileID = (id: string | null) => {
    localStorage.setItem('profileID', id || '')
    LoginService.setImpersonationProfileID(id)
    setImposterProfileIDInternal(id)
  }

  const clearImposterProfileID = () => {
    localStorage.setItem('profileID', '')
    LoginService.setImpersonationProfileID(null)
    setImposterProfileIDInternal(null)
  }

  const initialFilterState = useMemo(
    () => {
      // if ((minimumProfileLoaded && !fullProfileLoaded) || fullProfileLoaded)
      return getInitialSmartFilterState(profile)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      minimumProfileLoaded,
      fullProfileLoaded,
      minimumFilterOptionsLoaded,
      fullFilterOptionsLoaded,
    ]
  )

  const getFilterOptions = useCallback(
    async (data?: Partial<TProfile>): Promise<TFilterState> => {
      const options = await ProfileUtilities.getOptionsFromProfile(
        data || profile
      )
      setFilterOptions(options || { ...defaultFilterState })
      setAllFilterOptions(options || { ...defaultFilterState })
      return options
    },
    [profile]
  )

  //handle filtering the current options based on the organization scope.
  useEffect(() => {
    if (organizationScope == null) {
      setUserDetails(prev => ({
        ...prev,
        isMultiCustomerUser: allFilterOptions.customers.length > 1,
        isMultiPlantUser: allFilterOptions.plants.length > 1,
      }))

      setFilterOptions(allFilterOptions || { ...defaultFilterState })
      return
    }

    const filterPlants =
      organizationScope.plantIDs.size > 0
        ? allFilterOptions.plants.filter(p =>
            organizationScope.plantIDs.has(p.plantID)
          )
        : allFilterOptions.plants

    let newOptions = filterProfileOptions(
      {
        customers: allFilterOptions.customers.filter(
          c => c.custID == organizationScope.customerID
        ),
        plants: filterPlants,
        ...allFilterOptions,
      },
      allFilterOptions
    )

    setFilterOptions(newOptions || { ...defaultFilterState })

    setUserDetails(prev => ({
      ...prev,
      isMultiCustomerUser:
        organizationScope.customerID != null && newOptions.customers.length > 1,
      isMultiPlantUser:
        organizationScope.plantIDs == null
          ? newOptions.plants.length > 1
          : organizationScope.plantIDs?.size > 1,
    }))
  }, [
    organizationScope,
    organizationScope?.plantIDs,
    organizationScope?.customerID,
    allFilterOptions,
  ])

  const onMinimumProfileLoad = useCallback(
    async (data: Partial<TProfile>) => {
      ReactDOM.unstable_batchedUpdates(async () => {
        await getFilterOptions(data)

        const permissionsLoaded = permissions.loaded
        const permissionsHaveError = !!permissions.error
        const hasAdminPermission = permissions.permissions.has(
          XAPIPermissions.Administrator
        )

        //NOTE: We can't get the impersonated user's permissions, only the signed-in user's... Imposters will thus always see admin. We may need this in user metadata.
        const hasImposter = permissions.permissions.has(
          XAPIPermissions.ImpersonateDatasightUser
        )

        const isReadOnly =
          !permissionsLoaded ||
          permissionsHaveError ||
          !(hasAdminPermission || hasImposter)

        setProfile(prev => ({
          ...prev,
          ...data,
          user: { ...data.user, readOnly: isReadOnly },
        }))

        const isDivisionUser = data.divisions?.length > 0
        const isMultiCustomerUser = data.customers?.length > 1
        const isMultiPlantUser = data.plants.length > 1
        const isFinancialRestrictedUser = isFinancialRestricted(
          data.customers.map(c => c.custID),
          data.customers.map(c => c.industryCode)
        )

        const isInternalUser = hasImposter

        setUserDetails(prev => ({
          ...prev,
          isDivisionUser,
          isUPSUser: data.customers?.some(customer => customer.uPSSI),
          isSullairUser: isSullairCustomer(data.customers.map(c => c.custID)),
          hasCompany: data.customers?.some(c => c.companyID !== null),
          isMultiCustomerUser,
          isMultiPlantUser,
          isReadonlyUser: isReadOnly,
          isUSSteelUser: isUSSteelCustomer(data.customers.map(c => c.custID)),
          isAnalyticsCustomer: isAnalyticsCustomer(data),
          isFinancialRestricted: isFinancialRestrictedUser,
          isInternalUser,
        }))
        setMinimumFilterOptionsLoaded(true)
        setMinimumProfileLoaded(true)
      })
    },
    [
      getFilterOptions,
      permissions.loaded,
      permissions.permissions,
      imposterProfileID,
    ]
  )

  const onFullProfileLoad = useCallback(
    async (data: Partial<TProfile>) => {
      ReactDOM.unstable_batchedUpdates(async () => {
        await getFilterOptions(data)
        setFullFilterOptionsLoaded(true)
      })
    },
    [getFilterOptions]
  )

  const loadProfile = useCallback(async () => {
    try {
      setProfileErrorState(null)
      setProfileLoading(true)

      await getFilterOptions() //Wait because of race condition with previous calls may lead to missing values.
      setInitialFilterOptionsLoaded(true)

      let promise = APIService.getMinimumProfile()
      let billingCustomerPromise = APIService.getBillingCustomerPromise()
      const bcData = await billingCustomerPromise

      setProfile(prev => ({ ...prev, ...bcData }))
      ongoingCancellablePromises.filter(p => p != promise)
      setBillingCustomersLoaded(true)

      ongoingCancellablePromises.push(promise)
      ongoingCancellablePromises.push(billingCustomerPromise)
      let data = await promise
      ongoingCancellablePromises.filter(p => p != promise)

      await onMinimumProfileLoad(data) //Wait because of race condition with previous calls may lead to missing values.

      promise = APIService.getFullProfile(data)
      ongoingCancellablePromises.push(promise)
      data = await promise
      ongoingCancellablePromises.filter(p => p != promise)

      setProfile(prev => ({
        ...prev,
        ...data,
        user: { ...data.user, readOnly: prev.user.readOnly },
      }))
      setFullProfileLoaded(true)
      await onFullProfileLoad(data) //Wait because of race condition with previous calls may lead to missing values.
      setProfileLoading(false)
    } catch (err) {
      setProfileErrorState(err)
    }
  }, [
    getFilterOptions,
    onFullProfileLoad,
    onMinimumProfileLoad,
    permissions.loaded,
    permissions.error,
    permissions.permissions,
  ])

  //Handle profile Error
  useEffect(() => {
    if (profileErrorState == null || !permissions.loaded || !navigate) {
      return
    }

    try {
      if (
        typeof profileErrorState === 'object' &&
        'status' in profileErrorState &&
        (profileErrorState.status === 401 || profileErrorState.status === 403)
      ) {
        console.log('unauthorized')
        navigate('/unauthorized')
        return
      }
      if (
        permissions.loaded &&
        permissions.permissions.has(XAPIPermissions.ImpersonateDatasightUser)
      ) {
        if (imposterProfileID != null) {
          console.log('inactive 1')
          clearImposterProfileID()
          navigate('/inactive')
          return
        }
        console.log('Imposter')
        navigate(
          `/impersonate?PostLoginURL=${
            window.location.pathname
          }${encodeURIComponent(window.location.search)}`
        )
        return
      }
      if (
        typeof profileErrorState === 'object' &&
        'status' in profileErrorState &&
        profileErrorState.status === 404
      ) {
        console.log('inactive')
        navigate('/inactive')
        return
      }
      if (
        typeof profileErrorState === 'object' &&
        'status' in profileErrorState &&
        profileErrorState.status === 500
      ) {
        console.log('Something went wrong 1')
        navigate('/something-went-wrong')
        return
      }
      if (
        typeof profileErrorState === 'object' &&
        'message' in profileErrorState &&
        typeof profileErrorState.message == 'string' &&
        profileErrorState.message.includes('Failed to fetch')
      ) {
        console.log('Something went wrong 2')
        navigate('/something-went-wrong')
        return
      }
      console.log('Something ELSE went wrong')
      console.error('Something else went wrong! ', profileErrorState)
    } finally {
      setProfileErrorState(null)
    }
  }, [profileErrorState, permissions.loaded, permissions.permissions, navigate])
  //Set the customerId and plantID scope from local storage, if applicable.
  useEffect(() => {
    if (!minimumProfileLoaded) {
      return
    }

    if (organizationScope != null) {
      LocalStorageManager.setLocalCustomerIDScope(organizationScope.customerID)
      LocalStorageManager.setLocalPlantIDScope(organizationScope.plantIDs)
      return
    }

    if (organizationScope == null) {
      const customerID = LocalStorageManager.getLocalCustomerIDScope()
      const plantIDs = LocalStorageManager.getLocalPlantIDScope()

      const customerIDIsValid =
        customerID != null &&
        profile.customers.find(c => c.custID === customerID) != null

      if (!customerIDIsValid) {
        LocalStorageManager.setLocalCustomerIDScope(null)
      }

      const plantIDIsValid =
        plantIDs != null &&
        profile.plants.find(c => plantIDs.has(c.plantID)) != null

      if (!plantIDIsValid) {
        LocalStorageManager.setLocalPlantIDScope(null)
      }

      if (customerIDIsValid && plantIDIsValid) {
        setOrganizationScope(_prev => ({
          customerID,
          plantIDs,
        }))
      }
    }
  }, [
    minimumProfileLoaded,
    organizationScope,
    profile.customers,
    profile.plants,
  ])

  const clearProfile = useCallback(async () => {
    ReactDOM.unstable_batchedUpdates(() => {
      cancelAllPendingAPICalls()
      setMinimumProfileLoaded(false)
      setFullProfileLoaded(false)
      setProfileLoading(false)
      setProfile({ ...profileDefaults })
      setFilterOptions({ ...defaultFilterState })
      setAllFilterOptions({ ...defaultFilterState })
      setUserDetails({})
      setBillingCustomersLoaded(false)
    })
  }, [])

  const reloadProfile = useCallback(async () => {
    clearProfile()
    loadProfile()
  }, [clearProfile, loadProfile])

  /**Load the profile under the condition that it is not already loading and has not already loaded. */
  const constructProfile = useCallback(async () => {
    if (
      !profileLoading &&
      !minimumProfileLoaded &&
      permissions.loaded &&
      permissions.permissions &&
      isAuthenticated
    ) {
      clearProfile()
      loadProfile()
    }
  }, [
    clearProfile,
    loadProfile,
    minimumProfileLoaded,
    profileLoading,
    isAuthenticated,
    permissions.loaded,
    permissions.permissions,
  ])

  const cancelAllPendingAPICalls = () => {
    while (ongoingCancellablePromises.length > 0) {
      const promise = ongoingCancellablePromises.pop()
      promise.abortController?.abort()
    }
  }

  //NOTE This removes all ongoing, cancellable promises when the application is exited.
  useEffect(() => {
    return () => {
      cancelAllPendingAPICalls()
    }
  }, [])

  //!SECTION

  //SECTION Parse Dependent Data

  //!SECTION

  const reparseMachineLists = async () => {
    if (fullProfileLoaded) {
      setFullProfileLoaded(false)

      let tempProfile
      if (window?.Worker) {
        tempProfile = await getProfileParser().parseMachineLists(profile)
      } else {
        tempProfile = (await parseMachineLists(profile)) as TProfile
      }

      setProfile(prev => ({ ...prev, tempProfile }))

      const res = await ProfileUtilities.getOptionsFromProfile(profile)
      setFilterOptions(res || { ...defaultFilterState })
      setAllFilterOptions(res || { ...defaultFilterState })

      setFullProfileLoaded(true)
    }
  }

  const onLogoutEvents: TOnLogoutEvents = {
    AddEvent: func => {
      logoutEvents.push(func)
    },
    RemoveEvent: func => {
      logoutEvents = logoutEvents.filter(e => e !== func)
    },
  }

  return (
    <ProfileContext.Provider
      value={{
        readyToLoad: isAuthenticated && !!accessToken && !!permissions.loaded,
        fullProfileLoaded,
        minimumProfileLoaded,
        billingCustomersLoaded,
        profile,
        userSessionManagement: {
          onLogoutEvents,
        },
        dependentData: {
          userDetails: {
            ...userDetails,
            isMultiPlantUser: userDetails.isMultiPlantUser,
            isMultiCustomerUser: userDetails.isMultiCustomerUser,
            canImposter:
              permissions.loaded &&
              permissions.permissions.has(
                XAPIPermissions.ImpersonateDatasightUser
              ),
            imposterProfileID: imposterProfileID,
          },
          filters: {
            filterOptions: currentFilterOptions,
            defaultValues: defaultValues,
            initialFilterOptionsLoaded,
            organizationScope,
            minimumFilterOptionsLoaded,
            fullFilterOptionsLoaded,
            initialFilterState,
          },
        },
        utilities: {
          setImposterProfileID: setImposterProfileID,
          clearImposterProfileID: clearImposterProfileID,
          reloadProfile,
          constructProfile,
          clearProfile,
          // setUserLoggedIn,
          getFilterOptions,
          reparseMachineLists,
          setOrganizationScope,
        },
      }}
    >
      {props.children}
    </ProfileContext.Provider>
  )
}

const getInitialSmartFilterState = (profile: Partial<TProfile>) => {
  const division = profile?.divisions?.find(x =>
    x.customers.some(y => y.custID === profile?.customers[0].custID)
  )
  const selectedDivisionIDs =
    profile?.divisions?.length > 0 &&
    profile?.customers?.length > 0 &&
    division !== null &&
    division !== undefined
      ? [division.iD]
      : []

  const selectedCustomerIDs =
    profile?.customers?.length > 0 ? [profile?.customers[0].custID] : []

  return {
    divisions:
      profile?.divisions?.filter(d => selectedDivisionIDs.includes(d.iD)) || [],
    customers:
      profile?.customers?.filter(c => selectedCustomerIDs.includes(c.custID)) ||
      [],
    customerEquipmentIDs: [],
    lubricantConditions: [],
    lubricantManufacturers: [],
    lubricantTypes: [],
    machineConditions: [],
    machineManufacturers: [],
    machineModels: [],
    machineNames: [],
    machineNumbers: [],
    machinePicklistTypes: [],
    machines: [],
    plants: [],
    reportTypes: [],
    routes: [],
    samples: [],
    testPackages: [],
  } as Partial<TFilterState>
}
